import React, { useState, useEffect } from 'react'
import { Modal, Button, Icon, Input, Select, message } from 'antd'
import { useWardrobe } from '../../../../hooks/useWardrobe'
import { OutfitGrid, useOutfitLibrary } from './shared'
import CreateOutfitModal from './CreateOutfitModal'
import WardrobeLibraryPickerModal from './WardrobeLibraryPickerModal'
import './WardrobeManagerModal.scss'

const { Option } = Select

/**
 * Modal for managing the global wardrobe library
 */
const WardrobeManagerModal = ({
  visible,
  onClose,
  formData,
  setFormData,
  videoId
}) => {
  const {
    deleteOutfit,
    createOutfit,
    isCreatingOutfit: isCreating,
    creationProgress: progress,
    stopPolling
  } = useWardrobe({ videoId })

  const {
    outfits: libraryOutfits,
    isLoading: isLibraryLoading,
    fetchIfNeeded,
    refresh,
    pagination
  } = useOutfitLibrary({ formData, setFormData, videoId })

  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState({
    style: '',
    season: '',
    occasion: ''
  })
  const [sortBy, setSortBy] = useState('tag')
  const [sortOrder, setSortOrder] = useState('asc')
  const [pageSize, setPageSize] = useState(20)
  const [activeMode, setActiveMode] = useState('list')
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [selectedOutfit, setSelectedOutfit] = useState(null)

  useEffect(() => {
    if (visible) {
      fetchIfNeeded()
    }
    return () => stopPolling()
  }, [visible])

  const hasAnyFilter = !!filters.style || !!filters.season || !!filters.occasion

  const fetchWithCurrentMode = async ({
    mode = activeMode,
    page = 1,
    nextFilters = filters,
    nextQuery = searchQuery,
    nextSortBy = sortBy,
    nextSortOrder = sortOrder,
    nextPageSize = pageSize
  } = {}) => {
    const trimmedQuery = (nextQuery || '').trim()

    if (mode === 'search' && trimmedQuery) {
      setActiveMode('search')
      return refresh({
        action: 'search',
        q: trimmedQuery,
        page,
        page_size: nextPageSize
      })
    }

    const shouldUseFilter =
      mode === 'filter' &&
      (!!nextFilters.style || !!nextFilters.season || !!nextFilters.occasion)

    if (shouldUseFilter) {
      setActiveMode('filter')
      return refresh({
        action: 'filter',
        style: nextFilters.style || undefined,
        season: nextFilters.season || undefined,
        occasion: nextFilters.occasion || undefined,
        page,
        page_size: nextPageSize,
        sort_by: nextSortBy,
        sort_order: nextSortOrder
      })
    }

    setActiveMode('list')
    return refresh({
      action: 'list',
      page,
      page_size: nextPageSize,
      sort_by: nextSortBy,
      sort_order: nextSortOrder
    })
  }

  const handleSearch = () => {
    const trimmed = searchQuery.trim()
    if (trimmed) {
      fetchWithCurrentMode({ mode: 'search', page: 1, nextQuery: trimmed })
      return
    }
    fetchWithCurrentMode({ mode: hasAnyFilter ? 'filter' : 'list', page: 1 })
  }

  const handleFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value || '' }
    setFilters(newFilters)
    const shouldFilter =
      !!newFilters.style || !!newFilters.season || !!newFilters.occasion
    fetchWithCurrentMode({
      mode: shouldFilter ? 'filter' : 'list',
      page: 1,
      nextFilters: newFilters
    })
  }

  const handleSortBy = value => {
    const nextSortBy = value || 'tag'
    setSortBy(nextSortBy)
    fetchWithCurrentMode({
      mode: activeMode,
      page: 1,
      nextSortBy
    })
  }

  const handleSortOrder = value => {
    const nextSortOrder = value || 'asc'
    setSortOrder(nextSortOrder)
    fetchWithCurrentMode({
      mode: activeMode,
      page: 1,
      nextSortOrder
    })
  }

  const handlePageSizeChange = value => {
    const nextPageSize = Number(value) || 20
    setPageSize(nextPageSize)
    fetchWithCurrentMode({
      mode: activeMode,
      page: 1,
      nextPageSize
    })
  }

  const handlePageChange = page => {
    fetchWithCurrentMode({
      mode: activeMode,
      page
    })
  }

  const handleDelete = async outfit => {
    try {
      await deleteOutfit(outfit.tag || outfit.id)
      message.success('Outfit deleted')
      refresh()
    } catch (err) {
      message.error('Failed to delete outfit')
    }
  }

  const handleOutfitClick = outfit => {
    setSelectedOutfit(outfit)
  }

  return (
    <>
      <Modal
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={920}
        centered
        className="wardrobe-manager-modal"
        title={null}
        closable={false}
        destroyOnClose
      >
        <div className="manager-container">
          {/* Header */}
          <div className="manager-header">
            <h2 className="manager-title">Manage Wardrobe</h2>
            <div className="close-icon" onClick={onClose}>
              <Icon type="close" />
            </div>
          </div>

          {/* Toolbar */}
          <div className="manager-toolbar">
            <div className="search-section">
              <Input
                placeholder="Search outfits..."
                prefix={<Icon type="search" />}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onPressEnter={handleSearch}
                allowClear
              />
            </div>

            <div className="filter-section">
              <Select
                placeholder="All Styles"
                value={filters.style || undefined}
                onChange={val => handleFilter('style', val)}
                allowClear
                className="filter-select"
              >
                <Option value="formal">Formal</Option>
                <Option value="casual">Casual</Option>
                <Option value="sporty">Sporty</Option>
                <Option value="business">Business</Option>
              </Select>

              <Select
                placeholder="All Seasons"
                value={filters.season || undefined}
                onChange={val => handleFilter('season', val)}
                allowClear
                className="filter-select"
              >
                <Option value="summer">Summer</Option>
                <Option value="winter">Winter</Option>
                <Option value="spring">Spring</Option>
                <Option value="fall">Fall</Option>
                <Option value="all-season">All Season</Option>
              </Select>

              <Select
                placeholder="All Occasions"
                value={filters.occasion || undefined}
                onChange={val => handleFilter('occasion', val)}
                allowClear
                className="filter-select"
              >
                <Option value="work">Work</Option>
                <Option value="party">Party</Option>
                <Option value="casual">Casual</Option>
                <Option value="date">Date</Option>
                <Option value="beach">Beach</Option>
              </Select>

              <Select
                placeholder="Sort By"
                value={sortBy}
                onChange={handleSortBy}
                className="filter-select"
              >
                <Option value="tag">Tag</Option>
                <Option value="name">Name</Option>
                <Option value="style">Style</Option>
                <Option value="season">Season</Option>
                <Option value="occasion">Occasion</Option>
                <Option value="created_at">Created</Option>
                <Option value="view_count">Views</Option>
              </Select>

              <Select
                placeholder="Order"
                value={sortOrder}
                onChange={handleSortOrder}
                className="filter-select sort-order-select"
              >
                <Option value="asc">Asc</Option>
                <Option value="desc">Desc</Option>
              </Select>

              <Select
                placeholder="Page Size"
                value={pageSize}
                onChange={handlePageSizeChange}
                className="filter-select page-size-select"
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={50}>50</Option>
                <Option value={100}>100</Option>
              </Select>
            </div>
          </div>

          {/* Subheader */}
          <div className="manager-subheader">
            <div className="subheader-text">
              <h3>Wardrobe Library</h3>
              <p>Browse and manage your global collection of outfits.</p>
            </div>
            <div className="subheader-actions">
              <Button
                type="primary"
                icon="plus"
                onClick={() => setIsCreateModalVisible(true)}
              >
                Add New Outfit
              </Button>
              <Button
                icon="reload"
                onClick={() =>
                  fetchWithCurrentMode({ mode: activeMode, page: 1 })
                }
                loading={isLibraryLoading}
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Grid */}
          <div className="manager-content">
            <OutfitGrid
              outfits={libraryOutfits}
              onItemClick={handleOutfitClick}
              isLoading={isLibraryLoading}
              emptyMessage="No outfits found in your library. Add one to get started!"
              showDelete={true}
              onDelete={handleDelete}
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        </div>

        <CreateOutfitModal
          visible={isCreateModalVisible}
          onClose={() => setIsCreateModalVisible(false)}
          onSuccess={() => refresh()}
          createOutfit={createOutfit}
          isCreating={isCreating}
          creationProgress={progress}
        />
      </Modal>

      <WardrobeLibraryPickerModal
        visible={!!selectedOutfit}
        onClose={() => setSelectedOutfit(null)}
        initialOutfit={selectedOutfit}
        mode="view"
        formData={formData}
        setFormData={setFormData}
        videoId={videoId}
      />
    </>
  )
}

export default WardrobeManagerModal
